import { Complete } from "@/components/common/Complete";
import {
	useSetRoles,
	usersOrganisationsRolesQueryOptions,
} from "@/features/auth/api/selectUsersOrganisationsRoles";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { useAuth } from "@/utils/auth";
import { createFileRoute } from "@tanstack/react-router";
import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";

const OnboardingComplete = () => {
	const auth = useAuth();
	const { retry } = useSetRoles();
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const timer = setTimeout(() => {
			retry();
			setIsLoading(false);
		}, 2000);

		return () => clearTimeout(timer);
	}, [retry]);

	const getNextRoute = () => {
		if (auth.roles?.isPatient) return "/diaries";
		if (auth.roles?.isClinician) return "/patients";
		return "/profile";
	};

	return (
		<Complete
			title={
				isLoading
					? "Processing New User"
					: auth.roles?.hasRole
						? "Onboarding Complete"
						: "Authentication Error"
			}
			description={
				isLoading
					? "Just a moment while we set everything up..."
					: auth.roles?.hasRole
						? "You can now use Patient Watch to share your progress with your health team, ensuring you receive the best possible care! 🚀"
						: "Please send a message to support@patient-watch.com"
			}
			icon={isLoading ? undefined : auth.roles?.hasRole ? Check : X}
			isLoading={isLoading}
			loadingText="Almost there! 🚀"
			loadingDescription="Just a moment while we set everything up..."
			minLoadingTime={2000}
			primaryButton={{
				text: "Next",
				to: getNextRoute(),
			}}
			secondaryButton={
				!auth.roles?.hasRole
					? {
							text: "Retry",
							to: "#",
							showWhen: true,
						}
					: undefined
			}
		/>
	);
};

export const Route = createFileRoute("/onboarding/complete")({
	component: OnboardingComplete,
	loader: async ({ context }) => {
		const roles = await context?.queryClient?.ensureQueryData(
			usersOrganisationsRolesQueryOptions(context.auth.id),
		);
		roles ?? context.auth.roles?.setRoles(roles);

		const profile = await context?.queryClient?.ensureQueryData(
			selectProfileByIdQueryOptions(context.auth.id),
		);
		profile ?? context.auth.setIsSuperUser(profile?.is_super_user ?? false);
	},
});
